import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppUserRole } from '@kdental/shared';
import { api, type AppUser } from '../lib/api.js';
import { initials } from '../lib/format.js';
import { toast } from '../lib/toast-store.js';
import { QueryError } from '../components/query-error.js';

const ROLES: AppUserRole[] = ['rep', 'manager', 'admin'];

/**
 * Admin → user management. Set each user's role, Spire salesperson code, and
 * cost/margin visibility, or deactivate them — without touching the database.
 * Server enforces admin access; the route also redirects non-admins.
 */
export function AdminScreen() {
  const users = useQuery({ queryKey: ['admin-users'], queryFn: ({ signal }) => api.users.list(signal) });

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Admin · Users</h2>
          <div className="sub">{users.data ? `${users.data.length} users` : 'Manage roles & visibility'}</div>
        </div>
        <Link to="/me" className="back-btn">‹ Me</Link>
      </div>

      <div style={{ paddingTop: 8 }}>
        <AddUser />
        {users.isPending && Array.from({ length: 4 }).map((_, i) => <div key={i} className="kd-skeleton" style={{ height: 120 }} />)}
        {users.isError && <QueryError message={users.error.message} onRetry={() => void users.refetch()} />}
        {users.data?.map((u) => <UserRow key={u.id} user={u} />)}
      </div>
    </>
  );
}

function AddUser() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AppUserRole>('rep');
  const [code, setCode] = useState('');
  const [cost, setCost] = useState(false);
  const [margin, setMargin] = useState(true);
  const [override_, setOverride] = useState(true);
  const [orders_, setOrders] = useState(true);
  const [target, setTarget] = useState('');
  const [sendInvite, setSendInvite] = useState(true);

  const reset = () => {
    setEmail(''); setName(''); setRole('rep'); setCode(''); setCost(false); setMargin(true); setOverride(true); setOrders(true); setTarget(''); setSendInvite(true); setOpen(false);
  };
  const create = useMutation({
    mutationFn: async () => {
      const created = await api.users.create({
        email: email.trim(),
        displayName: name.trim(),
        role,
        spireSalespersonCode: code.trim() || undefined,
        canViewCost: cost,
        canViewMargin: margin,
        canOverridePrice: override_,
        canPlaceOrders: orders_,
        salesTargetAnnual: Math.max(0, Math.round(Number(target)) || 0),
      });
      if (sendInvite) {
        try {
          await api.users.invite(created.id);
          toast(`Invite emailed to ${created.email}`);
        } catch (e) {
          toast(e instanceof Error ? e.message : 'User added, but the invite failed', 'error');
        }
      }
      return created;
    },
    onSuccess: () => {
      toast(`Added ${name.trim()}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      reset();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not add user', 'error'),
  });

  const valid = /\S+@\S+\.\S+/.test(email.trim()) && name.trim().length > 0;

  if (!open) {
    return (
      <button type="button" className="show-more" onClick={() => setOpen(true)}>
        + Add user
      </button>
    );
  }
  return (
    <div className="kd-card">
      <div style={{ fontWeight: 600, color: 'var(--kd-navy)', marginBottom: 10 }}>New user</div>
      <div className="admin-grid">
        <label className="admin-field">
          <span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="rep@kdental.com" type="email" />
        </label>
        <label className="admin-field">
          <span>Display name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Rep" maxLength={80} />
        </label>
        <label className="admin-field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as AppUserRole)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <SalespersonSelect value={code} onChange={setCode} />
        <label className="admin-field">
          <span>Annual sales target ($, 0 = none)</span>
          <input type="number" min={0} step={1000} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 750000" />
        </label>
      </div>
      <div className="admin-toggles">
        <Toggle label="View cost" on={cost} onChange={setCost} />
        <Toggle label="View margin" on={margin} onChange={setMargin} />
        <Toggle label="Override price" on={override_} onChange={setOverride} />
        <Toggle label="Place orders (off = quotes only)" on={orders_} onChange={setOrders} />
        <Toggle label="Email login invite" on={sendInvite} onChange={setSendInvite} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" className="btn btn-secondary" disabled={create.isPending} onClick={reset}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </div>
  );
}

function UserRow({ user }: { user: AppUser }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<AppUserRole>(user.role as AppUserRole);
  const [code, setCode] = useState(user.spireSalespersonCode ?? '');
  const [cost, setCost] = useState(user.canViewCost);
  const [margin, setMargin] = useState(user.canViewMargin);
  const [override_, setOverride] = useState(user.canOverridePrice);
  const [orders_, setOrders] = useState(user.canPlaceOrders);
  const [target, setTarget] = useState(String(user.salesTargetAnnual || ''));
  const [active, setActive] = useState(user.active);

  const dirty =
    role !== user.role ||
    code.trim() !== (user.spireSalespersonCode ?? '') ||
    cost !== user.canViewCost ||
    margin !== user.canViewMargin ||
    override_ !== user.canOverridePrice ||
    orders_ !== user.canPlaceOrders ||
    (Math.round(Number(target)) || 0) !== user.salesTargetAnnual ||
    active !== user.active;

  const save = useMutation({
    mutationFn: () =>
      api.users.update(user.id, {
        role,
        spireSalespersonCode: code.trim(),
        canViewCost: cost,
        canViewMargin: margin,
        canOverridePrice: override_,
        canPlaceOrders: orders_,
        salesTargetAnnual: Math.max(0, Math.round(Number(target)) || 0),
        active,
      }),
    onSuccess: () => {
      toast(`Saved ${user.displayName}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Save failed', 'error'),
  });

  const invite = useMutation({
    mutationFn: () => api.users.invite(user.id),
    onSuccess: () => {
      toast(`Invite emailed to ${user.email}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Invite failed', 'error'),
  });

  return (
    <div className="kd-card" style={{ opacity: active ? 1 : 0.7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div className="avatar">{initials(user.displayName)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--kd-text)' }}>{user.displayName}</div>
          <div className="kd-muted" style={{ fontSize: 12 }}>{user.email}</div>
        </div>
        <LoginBadge status={user.loginStatus} />
      </div>
      {(user.loginStatus === 'none' || user.loginStatus === 'invited') && (
        <button
          type="button"
          className="btn btn-secondary"
          style={{ marginBottom: 10, fontSize: 13 }}
          disabled={invite.isPending}
          onClick={() => invite.mutate()}
        >
          {invite.isPending
            ? 'Sending…'
            : user.loginStatus === 'none'
              ? 'Create login & email invite'
              : 'Resend invite email'}
        </button>
      )}

      <div className="admin-grid">
        <label className="admin-field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as AppUserRole)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <SalespersonSelect value={code} onChange={setCode} />
        <label className="admin-field">
          <span>Annual sales target ($, 0 = none)</span>
          <input type="number" min={0} step={1000} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0" />
        </label>
      </div>

      <div className="admin-toggles">
        <Toggle label="View cost" on={cost} onChange={setCost} />
        <Toggle label="View margin" on={margin} onChange={setMargin} />
        <Toggle label="Override price" on={override_} onChange={setOverride} />
        <Toggle label="Place orders (off = quotes only)" on={orders_} onChange={setOrders} />
        <Toggle label="Active" on={active} onChange={setActive} />
      </div>

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 10 }}
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
      </button>
    </div>
  );
}

/** Login-state chip: has this person got a working sign-in? */
function LoginBadge({ status }: { status?: string }) {
  if (!status || status === 'unknown') return null; // dev IdP or lookup failure
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    none: { bg: '#fce4e1', fg: 'var(--kd-danger)', label: 'No login' },
    invited: { bg: '#fff1d9', fg: '#a8770a', label: 'Invited' },
    active: { bg: '#e0f4e9', fg: 'var(--kd-green-dark)', label: 'Login active' },
    disabled: { bg: '#eceff3', fg: 'var(--kd-muted, #667)', label: 'Login disabled' },
  };
  const s = styles[status];
  if (!s) return null;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 999,
        padding: '3px 9px',
        whiteSpace: 'nowrap',
        background: s.bg,
        color: s.fg,
      }}
    >
      {s.label}
    </span>
  );
}

/**
 * Salesperson picker — the live Spire roster instead of a free-text code, so
 * codes can't be mistyped (they're 10-digit numerics at K-Dental). A stored
 * value that's no longer in the roster is kept selectable, labeled (legacy).
 * Falls back to a text input if the roster can't load.
 */
function SalespersonSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const roster = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => api.users.salespeople(signal),
    staleTime: 10 * 60_000,
  });

  if (roster.isError) {
    return (
      <label className="admin-field">
        <span>Salesperson code (roster unavailable)</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={20} />
      </label>
    );
  }

  const list = roster.data ?? [];
  const known = list.some((s) => s.code === value);
  return (
    <label className="admin-field">
      <span>Salesperson</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={roster.isPending}>
        <option value="">— none (manager/admin) —</option>
        {value && !known && !roster.isPending && (
          <option value={value}>{value} (legacy — not in Spire)</option>
        )}
        {list.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name && s.name !== s.code ? `${s.name} (${s.code})` : s.code}
          </option>
        ))}
      </select>
    </label>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      className={`admin-toggle${on ? ' on' : ''}`}
      aria-pressed={on}
      onClick={() => onChange(!on)}
    >
      <span className="dot" />
      {label}
    </button>
  );
}
